<template>
  <!-- Zahlungsmethode im eingebetteten Stripe Payment Element ändern
       (Spec 2026-08-17) — kein Absprung ins Kundenportal mehr. -->
  <div aria-modal="true" class="pmd" role="dialog" @click.self="close">
    <div class="pmd__panel">
      <header class="pmd__head">
        <h3>{{ required ? $gt('Zahlungsmethode hinterlegen') : $gt('Zahlungsmethode ändern') }}</h3>
        <button
          v-if="!required"
          :aria-label="$gt('Schließen')"
          class="pmd__close"
          type="button"
          @click="close"
        >
          <X :size="16" />
        </button>
      </header>

      <!-- Pflicht-Modus: Erklärung, warum die Zahlungsmethode nötig ist. -->
      <p v-if="intro" class="pmd__intro">{{ intro }}</p>
      <p v-if="notice" class="pmd__error pmd__error--top">{{ notice }}</p>

      <p class="pmd__note">
        <ShieldCheck :size="13" />
        {{ $gt('Ihre Zahlungsdaten werden direkt und verschlüsselt an unseren Zahlungsdienstleister Stripe übertragen – KARS speichert sie nicht.') }}
      </p>

      <!-- Stripe mountet das Payment Element in diesen Container. -->
      <div ref="elementHost" class="pmd__element" />

      <div v-if="initializing" class="pmd__loading">
        {{ $gt('Sicheres Zahlungsformular wird geladen…') }}
      </div>
      <p v-if="errorMessage" class="pmd__error">{{ errorMessage }}</p>

      <!-- Zahlungsformular nicht geladen (Backend/Stripe-Fehler): Speichern
           wäre wirkungslos. Stattdessen erneut versuchen – und auch im
           Pflicht-Modus einen Ausweg bieten, sonst bleibt nur Abmelden
           (Fehlermeldung 16.10.2026). -->
      <div v-if="initFailed" class="pmd__actions">
        <button
          class="pmd__btn pmd__btn--primary"
          data-testid="pmd-retry"
          :disabled="initializing"
          type="button"
          @click="retry"
        >
          {{ $gt('Erneut versuchen') }}
        </button>
        <button
          class="pmd__btn"
          data-testid="pmd-later"
          type="button"
          @click="required ? giveUp() : close()"
        >
          {{ required ? $gt('Später erneut versuchen') : $gt('Abbrechen') }}
        </button>
      </div>
      <div v-else class="pmd__actions">
        <button
          class="pmd__btn pmd__btn--primary"
          data-testid="pmd-save"
          :disabled="initializing || submitting || !ready"
          type="button"
          @click="submit"
        >
          {{ submitting ? $gt('Wird gespeichert…') : (required ? $gt('Zahlungsmethode speichern') : $gt('Als neue Zahlungsmethode speichern')) }}
        </button>
        <button
          v-if="!required"
          class="pmd__btn"
          :disabled="submitting"
          type="button"
          @click="close"
        >
          {{ $gt('Abbrechen') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ShieldCheck, X } from 'lucide-vue-next'
  import { onBeforeUnmount, onMounted, ref } from 'vue'
  import { gt } from '@/i18n/gt'
  import { confirmPaymentMethod, createPaymentMethodSetup } from '@/services/billingApi'
  import { paymentMethodErrorMessage } from '@/pages/pageInvoices/paymentMethodError'

  const props = defineProps({
    /* Unternehmen, dessen Zahlungsmethode eingerichtet wird. */
    idCompany: { type: Number, default: null },
    /* Pflicht-Modus (Zahlungsmethode fehlt): nicht schließbar. */
    required: { type: Boolean, default: false },
    /* Rücksprung nach PayPal; die Zielseite bestätigt den SetupIntent. */
    returnPath: { type: String, default: '/rechnungen?setup=done' },
    /* Erklärtext über dem Formular (Pflicht-Modus). */
    intro: { type: String, default: '' },
    /* Hinweis des Aufrufers, z. B. wenn das Anlegen der Abos scheiterte. */
    notice: { type: String, default: '' },
  })

  // 'close' trägt bei einem gescheiterten Formular-Aufbau { failed, message },
  // damit der Aufrufer den Grund anzeigen kann (Pflicht-Modus).
  const emit = defineEmits(['close', 'updated'])

  const elementHost = ref(null)
  const initializing = ref(true)
  const submitting = ref(false)
  const errorMessage = ref('')
  /* Zahlungsformular (SetupIntent + Stripe.js) ließ sich nicht aufbauen. */
  const initFailed = ref(false)
  /* Payment Element ist gemountet, Speichern ist möglich. */
  const ready = ref(false)

  let stripeClient = null
  let elements = null

  function close () {
    if (submitting.value || props.required) return
    emit('close')
  }

  /* Pflicht-Modus nach gescheitertem Formular-Aufbau: Der Aufrufer entscheidet,
     wie es weitergeht (Onboarding: im Schritt bleiben; Gate: nächster Abgleich). */
  function giveUp () {
    emit('close', { failed: true, message: errorMessage.value })
  }

  async function init () {
    initializing.value = true
    initFailed.value = false
    ready.value = false
    errorMessage.value = ''
    try {
      const { clientSecret, publishableKey } = await createPaymentMethodSetup(props.idCompany ?? undefined)
      // Dynamischer Import: lädt Stripe.js von js.stripe.com erst beim
      // Öffnen des Dialogs (von Stripe so vorgeschrieben).
      const { loadStripe } = await import('@stripe/stripe-js')
      stripeClient = await loadStripe(publishableKey)
      if (!stripeClient) throw new Error(gt('Stripe.js konnte nicht geladen werden.'))

      elements = stripeClient.elements({
        clientSecret,
        locale: 'de',
        appearance: {
          variables: {
            colorPrimary: '#0076e5',
            borderRadius: '10px',
            fontSizeBase: '15px',
          },
        },
      })
      elements.create('payment').mount(elementHost.value)
      ready.value = true
    } catch (error_) {
      // Ursache aus dem Backend (z. B. Stripe-Kunde/SetupIntent) statt der
      // Pauschalmeldung „interner Fehler".
      initFailed.value = true
      errorMessage.value = paymentMethodErrorMessage(error_, gt('Das Zahlungsformular konnte nicht geladen werden.'))
    } finally {
      initializing.value = false
    }
  }

  function teardown () {
    // Stripe-iframe sauber abbauen.
    try {
      elements?.getElement('payment')?.unmount()
    } catch {
      /* Element war nie gemountet. */
    }
    elements = null
    stripeClient = null
  }

  function retry () {
    teardown()
    return init()
  }

  onMounted(init)
  onBeforeUnmount(teardown)

  async function submit () {
    if (!ready.value || !stripeClient || !elements || submitting.value) return
    submitting.value = true
    errorMessage.value = ''
    try {
      // PayPal braucht einen Redirect (mit Rücksprung hierher); Karte und
      // SEPA laufen inline und liefern den SetupIntent direkt zurück.
      const { error, setupIntent } = await stripeClient.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}${props.returnPath}`,
        },
        redirect: 'if_required',
      })
      if (error) {
        // Validierungs-/Zahlungsfehler lokalisiert anzeigen, Dialog offen lassen.
        errorMessage.value = error.message || gt('Die Zahlungsmethode konnte nicht gespeichert werden.')
        return
      }
      const state = await confirmPaymentMethod(setupIntent.id, props.idCompany ?? undefined)
      emit('updated', state)
    } catch (error_) {
      errorMessage.value = paymentMethodErrorMessage(error_, gt('Die Zahlungsmethode konnte nicht gespeichert werden.'))
    } finally {
      submitting.value = false
    }
  }
</script>

<style scoped lang="scss">
.pmd {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.45);
}

.pmd__panel {
  width: min(460px, 100%);
  max-height: 90vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.25);
}

.pmd__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;

  h3 {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 750;
    color: var(--dark-color, #323232);
  }
}

.pmd__close {
  display: inline-flex;
  padding: 6px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--grey-color, #5f5f5f);
  cursor: pointer;

  &:hover {
    background: var(--grey-light-color, #f3f5f7);
  }
}

.pmd__note {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin: 0 0 14px;
  font-size: 0.78rem;
  line-height: 1.45;
  color: var(--grey-color, #5f5f5f);

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: var(--primary-color, #0076e5);
  }
}

.pmd__intro {
  margin: 0 0 12px;
  font-size: 0.88rem;
  line-height: 1.5;
  color: var(--dark-color, #323232);
}

.pmd__error--top {
  margin: 0 0 12px;
}

.pmd__element {
  min-height: 0;
}

.pmd__loading {
  padding: 18px 0;
  font-size: 0.85rem;
  color: var(--grey-color, #5f5f5f);
}

.pmd__error {
  margin: 10px 0 0;
  font-size: 0.82rem;
  color: var(--error-color, #d92d20);
}

.pmd__actions {
  display: flex;
  gap: 8px;
  margin-top: 16px;
}

.pmd__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 9px 15px;
  border: 1px solid var(--border-grey-color, #d0d5dd);
  border-radius: 10px;
  background: #fff;
  color: var(--dark-color, #323232);
  font-size: 0.85rem;
  font-weight: 650;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--grey-light-color, #f3f5f7);
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
}

.pmd__btn--primary {
  flex: 1;
  background: var(--primary-color, #0076e5);
  border-color: var(--primary-color, #0076e5);
  color: #fff;

  &:hover:not(:disabled) {
    background: #0069cc;
  }
}
</style>
